import { MASS_SPECS } from '../core/constants';
import { type LevelTrace, canPlace, remaining, traceLevel, weightOf, wins } from '../core/level';
import { starsFor } from '../core/score';
import type { Level, Mass, MassKind } from '../core/types';

export interface LitChange {
  index: number;
  lit: boolean;
}

/**
 * One attempt at one level: what is placed, undo history, moves, hints and the current trace.
 * Pure game state (no DOM), so it is easy to test and to drive from mouse, touch or keyboard.
 */
export class Session {
  placed: Mass[] = [];
  moves = 0;
  hintsUsed = 0;
  won = false;
  trace: LevelTrace;
  private history: { placed: Mass[]; moves: number }[] = [];

  constructor(readonly level: Level) {
    this.trace = traceLevel(level, []);
  }

  get weight(): number {
    return weightOf(this.placed);
  }

  get tray(): Record<MassKind, number> {
    return remaining(this.level.inventory, this.placed);
  }

  /** Stars this solution would earn right now. */
  get stars(): 1 | 2 | 3 {
    return starsFor(this.weight, this.level.par, this.hintsUsed);
  }

  /** Trace with an optional extra (dragged) mass replacing index `skip`. Does not commit. */
  preview(extra: Mass | null, skip: number | null): LevelTrace {
    const masses = this.placed.filter((_, i) => i !== skip);
    if (extra) masses.push(extra);
    return traceLevel(this.level, masses);
  }

  canPlaceAt(kind: MassKind, x: number, y: number, ignore: number | null): boolean {
    return canPlace(this.level, this.placed, kind, x, y, ignore ?? -1);
  }

  private snapshot() {
    this.history.push({ placed: this.placed.map((m) => ({ ...m })), moves: this.moves });
    if (this.history.length > 200) this.history.shift();
  }

  /** Apply a new set of placements as one move. Returns telescope changes. */
  commit(next: Mass[], countMove = true): LitChange[] {
    this.snapshot();
    this.placed = next.map((m) => ({ ...m }));
    if (countMove) this.moves++;
    return this.retrace();
  }

  /** Replace placements without recording history (used while a keyboard nudge run continues). */
  amend(next: Mass[]): LitChange[] {
    this.placed = next.map((m) => ({ ...m }));
    return this.retrace();
  }

  /** Start a new history entry for an upcoming run of amend() calls. */
  beginRun(): void {
    this.snapshot();
    this.moves++;
  }

  undo(): LitChange[] | null {
    const prev = this.history.pop();
    if (!prev) return null;
    this.placed = prev.placed;
    this.moves = prev.moves;
    return this.retrace();
  }

  get canUndo(): boolean {
    return this.history.length > 0;
  }

  reset(): LitChange[] {
    if (this.placed.length === 0) return [];
    return this.commit([], false);
  }

  /** Next reference mass not yet revealed. */
  nextHint(revealed: number): Mass | null {
    return this.level.solution[revealed] ?? null;
  }

  private retrace(): LitChange[] {
    const before = this.trace.receivers.map((r) => r.lit);
    this.trace = traceLevel(this.level, this.placed);
    this.won = this.trace.won;
    const changes: LitChange[] = [];
    this.trace.receivers.forEach((r, i) => {
      if (r.lit !== before[i]) changes.push({ index: i, lit: r.lit });
    });
    return changes;
  }

  /**
   * A good default spot for a new mass (tap or keyboard): near the centre, clear of everything
   * and of beams, and never a spot that wins outright.
   */
  freeSpot(kind: MassKind): Mass {
    const r = MASS_SPECS[kind].radius;
    let best: { m: Mass; score: number } | null = null;
    for (let ring = 0; ring < 18; ring++) {
      for (let k = 0; k < Math.max(1, ring * 8); k++) {
        const a = (k / Math.max(1, ring * 8)) * Math.PI * 2;
        const x = 600 + Math.cos(a) * ring * 30;
        const y = 400 + Math.sin(a) * ring * 22;
        if (!this.canPlaceAt(kind, x, y, null)) continue;
        // Keep away from beams so the new mass does not block one on arrival.
        let clearance = Infinity;
        for (const b of this.trace.beams) {
          for (const run of b.runs) {
            for (let i = 0; i + 1 < run.pts.length; i += 2) {
              clearance = Math.min(clearance, Math.hypot(run.pts[i]! - x, run.pts[i + 1]! - y));
            }
          }
        }
        if (clearance < r + 18) continue;
        // Never drop a mass where it would solve the plate by itself: tapping is not solving.
        const m = { kind, x: Math.round(x), y: Math.round(y) };
        if (wins(this.level, [...this.placed, m])) continue;
        const score = ring * 30 - Math.min(clearance, 80) * 0.2;
        if (!best || score < best.score)
          best = { m: { kind, x: Math.round(x), y: Math.round(y) }, score };
      }
      if (best && ring > 2) break;
    }
    return best?.m ?? { kind, x: 600, y: 400 };
  }
}
